import React, { useState, useEffect } from 'react';
import './Services.css';
import { useModalScrollLock } from '../hooks/useModalScrollLock';

interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
  isSpecial?: boolean;
}

interface ComplianceItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  modalContent: {
    title: string;
    description: string;
    details: string[];
    regulations: string[];
    benefits: string[];
  };
}

const services: Service[] = [
  {
    icon: "🗺️",
    title: "Mapeo y Topografía",
    description: "Levantamientos topográficos de alta precisión utilizando tecnología LiDAR y fotogrametría.",
    features: [
      "Mapas topográficos detallados",
      "Modelos digitales de terreno",
      "Cálculo de volúmenes",
      "Precisión centimétrica"
    ]
  },
  {
    icon: "📸",
    title: "Fotografía Aérea",
    description: "Servicios de fotografía y videografía aérea profesional para diversos sectores.",
    features: [
      "Fotografía comercial",
      "Videos promocionales",
      "Inmobiliario",
      "Eventos especiales"
    ]
  },
  {
    icon: "🔍",
    title: "Inspecciones",
    description: "Inspección detallada de infraestructuras y equipos en lugares de difícil acceso.",
    features: [
      "Torres de telecomunicaciones",
      "Paneles solares",
      "Líneas eléctricas",
      "Estructuras industriales"
    ]
  },
  {
    icon: "📡",
    title: "Transmisiones en Vivo",
    description: "Transmisiones aéreas en tiempo real para eventos, seguimiento y monitoreo.",
    features: [
      "Eventos deportivos",
      "Conferencias en vivo",
      "Monitoreo de emergencias",
      "Streaming profesional"
    ]
  },
  {
    icon: "📹",
    title: "Cámara Aérea CCTV",
    description: "Sistemas de vigilancia aérea temporal para seguridad y monitoreo de perímetros.",
    features: [
      "Vigilancia perimetral",
      "Monitoreo de eventos",
      "Seguridad industrial",
      "Grabación nocturna"
    ]
  },
  {
    icon: "🔍",
    title: "Búsqueda con Drones",
    description: "Servicios especializados de búsqueda y rescate con tecnología térmica avanzada.",
    features: [
      "Búsqueda de personas",
      "Reconocimiento de terreno",
      "Cámara térmica",
      "Operaciones nocturnas"
    ]
  },
  {
    icon: "🛠️",
    title: "Proyectos Personalizados",
    description: "¿Tienes una necesidad específica? Desarrollamos soluciones personalizadas con drones para resolver lo que necesites.",
    features: [
      "Consultoría especializada",
      "Desarrollo de soluciones a medida",
      "Tecnología Mavic 4 Pro & Enterprise",
      "Soporte técnico completo"
    ],
    isSpecial: true
  }
];

const complianceItems: ComplianceItem[] = [
  {
    id: "anac",
    icon: "🛩️",
    title: "Certificaciones ANAC",
    description: "Pilotos habilitados oficialmente",
    modalContent: {
      title: "Certificaciones ANAC (Administración Nacional de Aviación Civil)",
      description: "Nuestros pilotos están certificados oficialmente por la ANAC para realizar operaciones comerciales con RPAS (Remotely Piloted Aircraft Systems).",
      details: [
        "Licencias de piloto RPAS vigentes y actualizadas",
        "Certificación según Resolución 527/2018 y modificatorias",
        "Formación continua en regulaciones aeronáuticas",
        "Habilitación para operaciones en todo el territorio argentino"
      ],
      regulations: [
        "Resolución ANAC 527/2018",
        "Decreto 1089/2018",
        "Código Aeronáutico Argentino (Ley 17.285)",
        "Resolución ANAC 798/2020"
      ],
      benefits: [
        "Operaciones legales y seguras",
        "Cumplimiento total de normativas",
        "Respaldo oficial para operaciones comerciales",
        "Tranquilidad para nuestros clientes"
      ]
    }
  },
  {
    id: "rorp",
    icon: "📋",
    title: "RORP Vigente",
    description: "Registro oficial de operador",
    modalContent: {
      title: "RORP (Registro de Operador de RPAS)",
      description: "Contamos con el Registro de Operador de RPAS vigente, que nos habilita legalmente para realizar operaciones comerciales con drones en toda Argentina.",
      details: [
        "Registro vigente conforme Decreto 1089/2018",
        "Habilitación comercial según Resolución ANAC 798/2020",
        "Autorización para operaciones en todo el país",
        "Registro actualizado anualmente"
      ],
      regulations: [
        "Decreto 1089/2018",
        "Resolución ANAC 798/2020",
        "Disposición ANAC 84/2020",
        "Normativa técnica RAAC Parte 101"
      ],
      benefits: [
        "Operaciones comerciales autorizadas",
        "Cumplimiento regulatorio completo",
        "Acceso a espacios aéreos controlados",
        "Legitimidad empresarial"
      ]
    }
  },
  {
    id: "seguro",
    icon: "🛡️",
    title: "Seguro RC",
    description: "Cobertura de responsabilidad civil",
    modalContent: {
      title: "Seguro de Responsabilidad Civil",
      description: "Mantenemos una póliza de seguro de responsabilidad civil vigente que cumple con todos los requerimientos del Código Aeronáutico Argentino.",
      details: [
        "Cobertura según Código Aeronáutico Argentino",
        "Protección para clientes y terceros",
        "Póliza vigente durante todas las operaciones",
        "Cobertura en todo el territorio nacional"
      ],
      regulations: [
        "Código Aeronáutico Argentino (Ley 17.285)",
        "Decreto 1089/2018",
        "Resolución ANAC 527/2018",
        "Normativas de seguros aeronáuticos"
      ],
      benefits: [
        "Protección total para nuestros clientes",
        "Cobertura de daños a terceros",
        "Respaldo financiero en caso de incidentes",
        "Tranquilidad y confianza en nuestros servicios"
      ]
    }
  }
];

const Services: React.FC = () => {
  const [selectedModal, setSelectedModal] = useState<string | null>(null);

  // Usar hook para manejar scroll de modal
  useModalScrollLock(!!selectedModal);

  // Manejar cierre del modal con Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedModal) {
        setSelectedModal(null);
      }
    };

    if (selectedModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedModal]);

  const openModal = (itemId: string) => {
    setSelectedModal(itemId);
  };

  const closeModal = () => {
    setSelectedModal(null);
  };

  const selectedItem = complianceItems.find(item => item.id === selectedModal);

  return (
    <section id="services" className="services">
      <div className="services-container">
        <h2 className="services-title">Nuestros Servicios</h2>
        <p className="services-subtitle">
          Ofrecemos soluciones integrales con drones para diferentes industrias y necesidades
        </p>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className={`service-card ${service.isSpecial ? 'service-card-special' : ''}`}>
              <div className="service-icon">{service.icon}</div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              {service.isSpecial && (
                <div className="special-cta">
                  <a href="#contact" className="special-btn">Consultar Proyecto</a>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="compliance-section">
          <div className="compliance-header">
            <h3 className="compliance-title">Operación Legal y Certificada</h3>
            <p className="compliance-subtitle">
              Volamos con todas las autorizaciones necesarias para operaciones comerciales
            </p>
          </div>
          
          <div className="compliance-items">
            {complianceItems.map((item) => (
              <div
                key={item.id}
                className="compliance-item"
                role="button"
                tabIndex={0}
                onClick={() => openModal(item.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar' || event.key === 'Space') {
                    event.preventDefault();
                    openModal(item.id);
                  }
                }}
                aria-label={`${item.title} - ${item.description}`}
              >
                <div className="compliance-icon-minimal">{item.icon}</div>
                <div className="compliance-text">
                  <span className="compliance-title-minimal">{item.title}</span>
                  <span className="compliance-desc">{item.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal */}
        {selectedModal && selectedItem && (
          <div className="modal-overlay" onClick={closeModal}>
            <div
              className="modal-content modal-with-scroll"
              role="dialog"
              aria-modal="true"
              aria-labelledby={`${selectedItem.id}-modal-title`}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={closeModal} aria-label="Cerrar modal">×</button>
              
              <div className="modal-header">
                <div className="modal-icon">{selectedItem.icon}</div>
                <h3 id={`${selectedItem.id}-modal-title`} className="modal-title">{selectedItem.modalContent.title}</h3>
              </div>
              
              <div className="modal-body modal-with-scroll">
                <p className="modal-description">{selectedItem.modalContent.description}</p>
                
                <div className="modal-section">
                  <h4 className="modal-section-title">Detalles de Certificación</h4>
                  <ul className="modal-list">
                    {selectedItem.modalContent.details.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="modal-section">
                  <h4 className="modal-section-title">Marco Regulatorio</h4>
                  <ul className="modal-list">
                    {selectedItem.modalContent.regulations.map((regulation, index) => (
                      <li key={index}>{regulation}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="modal-section">
                  <h4 className="modal-section-title">Beneficios para el Cliente</h4>
                  <ul className="modal-list">
                    {selectedItem.modalContent.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;